import type { DeveloperPortalTokens } from '@keboola/api-client/developerPortal';

/**
 * The original app persisted through `redux-persist` with a nested whitelist that kept exactly
 * `auth.tokens` and `auth.profile` — everything else (apps, vendors, users) was refetched on every
 * reload. TanStack Query now owns that server state, so only the token trio is persisted here.
 *
 * Same storage medium (`localStorage`) and same scope, deliberately: session survival across a
 * browser restart is the behaviour users have, and narrowing it to `sessionStorage` would silently
 * sign everyone out on restart.
 */
const STORAGE_KEY = 'keboola-developer-portal/session';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/**
 * A trio counts as a session only when all three tokens carry something.
 *
 * - Not an authorization check — the server is. A hand-written trio of `"x"` values passes this and
 *   is refused on the wire like any other bad token.
 * - The emptiness check earns its place elsewhere: `beforeLoad` guards test `session.get() !== null`,
 *   so a blank trio would satisfy them and render a frame of dashboard chrome before the 401 path
 *   caught up and signed the user out.
 */
const isTokenTrio = (value: unknown): value is DeveloperPortalTokens =>
  isRecord(value) &&
  isNonEmptyString(value.token) &&
  isNonEmptyString(value.accessToken) &&
  isNonEmptyString(value.refreshToken);

/**
 * Reads a persisted session. Anything unexpected — absent, unparseable, or the wrong shape after a
 * format change — reads as "signed out" rather than throwing, so a bad entry can never brick startup.
 * Access is wrapped because `localStorage` throws outright in a partitioned or cookie-blocked
 * context, not just when empty.
 */
export const readPersistedTokens = (): DeveloperPortalTokens | null => {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return null;
    const parsed: unknown = JSON.parse(raw);
    return isTokenTrio(parsed) ? parsed : null;
  } catch (error) {
    // Never fatal: an unreadable session just means "sign in again".
    console.warn('Could not read the persisted Developer Portal session.', error);
    return null;
  }
};

/** Writes (or clears, on `null`) the persisted session. Failures are logged, never thrown. */
export const persistTokens = (tokens: DeveloperPortalTokens | null): void => {
  try {
    if (tokens === null) {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
      return;
    }
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch (error) {
    // Storage full or blocked — the in-memory session still works for this tab.
    console.warn('Could not persist the Developer Portal session.', error);
  }
};
